import { type FormEvent } from 'react'
import { Head, useForm, usePage } from '@inertiajs/react'
import type { SharedPageProps } from '@/types/shared'
import { Input } from '@/components/ui/input'
import { HyrinWordmark } from '@/components/Shared/HyrinWordmark'
import { InlineErrorBanner } from '@/components/Shared/InlineErrorBanner'

interface ClaimUsernameProps {
  suggestedUsername: string
}

// Type alias (not interface): usePage<T>'s PageProps constraint needs the
// implicit index signature only object-literal types carry.
type ClaimUsernamePageProps = SharedPageProps & {
  errors?: { base?: string | string[] }
}

export default function ClaimUsername({ suggestedUsername }: ClaimUsernameProps) {
  const { errors: pageErrors } = usePage<ClaimUsernamePageProps>().props
  const form = useForm({ username: suggestedUsername })

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    form.post('/claim-username')
  }

  return (
    <>
      <Head title="Claim your username — Hyrin" />

      <div className="min-h-screen bg-muted">
        <header className="flex h-16 items-center border-b border-border px-9">
          <div className="flex items-center gap-2">
            <HyrinWordmark />
          </div>
        </header>

        <main className="mx-auto flex max-w-[520px] flex-col items-center px-6 py-[80px]">
          <div className="w-full rounded-card border border-border bg-background p-[18px]">
            <p className="mb-[14px] font-mono text-[11px] uppercase tracking-eyebrow text-primary">
              Claim your username
            </p>
            <h1 className="mb-[10px] font-heading text-[30px] font-bold leading-[1.1] tracking-[-0.02em] text-foreground">
              Pick your Hyrin address
            </h1>
            <p className="mb-6 font-sans text-base leading-[1.55] text-muted-foreground">
              This becomes your public profile URL — you can share it with anyone.
            </p>

            <p className="mb-6 font-mono text-[11px] text-muted-foreground">
              hyrin.ai/ind/{form.data.username.trim().toLowerCase()}
            </p>

            <InlineErrorBanner message={pageErrors?.base} />

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              <div className="flex flex-col gap-[6px]">
                <label
                  htmlFor="username"
                  className="font-sans text-[13px] font-medium text-foreground"
                >
                  Username
                </label>
                <Input
                  id="username"
                  type="text"
                  value={form.data.username}
                  onChange={(e) => form.setData('username', e.target.value)}
                  disabled={form.processing}
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={form.processing}
                className="inline-flex h-[46px] items-center justify-center rounded-cta bg-primary px-[22px] font-sans text-[15px] font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {form.processing ? 'Claiming…' : 'Claim username'}
              </button>
            </form>
          </div>
        </main>
      </div>
    </>
  )
}
